import React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';

import DownloadApplication from '@/ui/DownloadApplication/DownloadApplication';
import { generateLocaleUrl } from '@/utils/seo';

import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import CheckoutHeader from '../CheckoutHeader/CheckoutHeader';

function Layout({
  navbar,
  footer,
  appDownload,
  metadata,
  children,
  withoutTheme,
}) {
  const router = useRouter();
  const { asPath } = router;
  const languages = ['az', 'en', 'ru'];
  const getMarqueeData = (navbar) => {
    return Object.keys(navbar || {})
      .filter((key) =>
        ['show_marquee', 'marquee_text', 'marquee_icon'].includes(key),
      )
      .reduce((acc, key) => {
        acc[key] = navbar[key];
        return acc;
      }, {});
  };

  const generateCanonicalLinks = () => {
    return languages.map((lang) => (
      <link
        key={lang}
        rel='alternate'
        href={generateLocaleUrl(lang, asPath)}
        hrefLang={lang}
      />
    ));
  };

  return (
    <React.Fragment>
      <Head>
        <meta
          name='robots'
          content={
            metadata?.robots_content ||
            'follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large'
          }
        />
        <meta
          name='google-site-verification'
          content='5kO1HJLl47-oXLH6kpDzOfp2rk8tgmPpMyPXqSvxVSM'
        />
        <link rel='canonical' href={generateLocaleUrl(router.locale, asPath)} />
        {generateCanonicalLinks()}
        <link
          rel='alternate'
          href={`https://www.bakcell.com/${asPath}`}
          hrefLang='x-default'
        />
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1, maximum-scale=1.0'
        />
        {/* OpenGraph Cards */}
        <meta
          property='og:locale'
          content={`${router.locale}_${router.locale.toLocaleUpperCase()}`}
        />
        <meta property='og:type' content='website' />
        <meta
          property='og:title'
          content={
            metadata?.open_graph_card?.properties?.title || metadata?.title
          }
        />
        <meta
          property='og:description'
          content={
            metadata?.open_graph_card?.properties?.description ||
            metadata?.description
          }
        />
        <meta property='og:url' content={'https://www.bakcell.com/'} />
        <meta property='og:site_name' content='Bakcell' />
        <meta
          property='og:image'
          content={
            metadata?.open_graph_card?.properties?.image || '/assets/bkc.svg'
          }
        />
        <meta
          property='og:image:secure_url'
          content={
            metadata?.open_graph_card?.properties?.image_secure_url ||
            '/assets/bkc.svg'
          }
        />
        <meta
          property='og:image:width'
          content={metadata?.open_graph_card?.properties?.image_width || '1200'}
        />
        <meta
          property='og:image:height'
          content={metadata?.open_graph_card?.properties?.image_height || '630'}
        />
        <meta
          property='og:image:type'
          content={
            metadata?.open_graph_card?.properties?.image_type || 'image/svg'
          }
        />
      </Head>
      <div>
        <DownloadApplication data={appDownload} />
        {navbar ? (
          <Header
            bundle={navbar?.top_right_links}
            join={navbar?.join_modal}
            navigation={navbar?.links}
            toolbar={navbar?.additional_menu}
            topLinks={navbar?.top_left_links}
            marqueeData={getMarqueeData(navbar)}
            withoutTheme={withoutTheme}
            itemScope
            itemType='https://schema.org/Organization'
          />
        ) : (
          <CheckoutHeader />
        )}
        <div className={'page-wrapper'}>{children}</div>
        {footer && (
          <Footer
            data={footer}
            navigation={footer?.navigation?.top}
            itemScope
            itemType='https://schema.org/Organization'
          />
        )}
      </div>
    </React.Fragment>
  );
}

export default Layout;
